import React, { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import rawData from '../data/tarkovData.json';
import { slugify } from '../utils/slugs';
import { getLanguageFromPath, localizedPath } from '../utils/routes';

const labels = {
  en: {
    fallbackTitle: 'Quest guide',
    objectives: 'Objectives',
    trader: 'Trader',
    map: 'Map',
    openTracker: 'Open quest tracker',
    notFound: 'Quest guide not found',
  },
  tr: {
    fallbackTitle: 'Görev rehberi',
    objectives: 'Hedefler',
    trader: 'Trader',
    map: 'Harita',
    openTracker: 'Görev takipçisini aç',
    notFound: 'Görev rehberi bulunamadı',
  },
  ru: {
    fallbackTitle: 'Гайд по квесту',
    objectives: 'Цели',
    trader: 'Торговец',
    map: 'Карта',
    openTracker: 'Открыть трекер квестов',
    notFound: 'Гайд по квесту не найден',
  },
};

const findQuest = (questSlug, lang) => {
  const quests = Object.values(rawData.tasks || {});
  return quests.find((quest) => {
    const locale = quest.locales?.[lang] || quest.locales?.en;
    const names = [
      quest.name,
      quest.locales?.en?.name,
      quest.locales?.tr?.name,
      quest.locales?.ru?.name,
      locale?.name,
    ];

    return names.filter(Boolean).some((name) => {
      const baseSlug = slugify(name);
      return baseSlug === questSlug || `${baseSlug}-${quest.id}` === questSlug;
    });
  });
};

function QuestLanding() {
  const location = useLocation();
  const { questSlug } = useParams();
  const lang = getLanguageFromPath(location.pathname) || 'en';
  const text = labels[lang] || labels.en;
  const quest = useMemo(() => findQuest(questSlug, lang), [questSlug, lang]);
  const trackerPath = localizedPath('quests', lang);

  if (!quest) {
    return (
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-black text-white mb-4">{text.notFound}</h1>
        <Link to={trackerPath} className="text-emerald-400 font-bold hover:text-emerald-300">
          {text.openTracker}
        </Link>
      </main>
    );
  }

  const locale = quest.locales?.[lang] || quest.locales?.en || {};
  const enLocale = quest.locales?.en || {};
  const title = locale.name || quest.name || text.fallbackTitle;
  const objectives = (locale.objectives || enLocale.objectives || quest.objectives || []).slice(0, 8);
  const mapName = locale.mapName || enLocale.mapName || quest.map?.name || 'Multiple';

  return (
    <main className="max-w-5xl mx-auto px-6 py-12">
      <div className="bg-[#09090b]/90 border border-zinc-800 rounded-sm p-6 md:p-8 shadow-2xl">
        <div className="text-emerald-400 text-xs font-black uppercase tracking-[0.2em] mb-3">
          Escape from Tarkov
        </div>
        <article>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-4">
            {title} - {text.fallbackTitle}
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-3xl">
            {title} progression guide for Escape from Tarkov with trader context, map information, and objective steps for EN/TR/RU players.
          </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 text-sm">
          <div className="bg-black/40 border border-zinc-800 rounded-sm p-3">
            <div className="text-zinc-500 uppercase text-[10px] font-bold tracking-widest">{text.trader}</div>
            <div className="text-white font-bold">{quest.trader?.name || '-'}</div>
          </div>
          <div className="bg-black/40 border border-zinc-800 rounded-sm p-3">
            <div className="text-zinc-500 uppercase text-[10px] font-bold tracking-widest">{text.map}</div>
            <div className="text-white font-bold">{mapName}</div>
          </div>
        </div>

        <section aria-labelledby="quest-objectives">
          <h2 id="quest-objectives" className="text-xl font-black text-white mb-4">{text.objectives}</h2>
          <p className="text-sm text-zinc-500 leading-relaxed mb-4">
            Use these objectives to understand what must be done before opening the interactive tracker and marking progress on your profile.
          </p>
          <ul className="space-y-3">
            {objectives.map((objective) => (
              <li key={objective.id} className="border border-zinc-800 bg-black/30 rounded-sm p-3 text-zinc-300">
                {objective.description}
              </li>
            ))}
          </ul>
        </section>
        </article>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={trackerPath} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm font-bold">
            {text.openTracker}
          </Link>
          {quest.wikiLink && (
            <a href={quest.wikiLink} target="_blank" rel="noreferrer" className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm font-bold">
              Wiki
            </a>
          )}
        </div>
      </div>
    </main>
  );
}

export default QuestLanding;
